import { materialTypeFormat } from '@/common/material-type-formatting';
import { hasValue } from '@curby/shared';
import {
	Button,
	Divider,
	Flex,
	Input,
	LoadingOverlay,
	SegmentedControl,
	Select,
	Text,
} from '@mantine/core';
import { DatePickerInput } from '@mantine/dates';
import { IconX } from '@tabler/icons-react';
import { useNavigate, useParams, useSearch } from '@tanstack/react-router';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { MaterialType } from '../../../../../backend/src/types.generated';
import { CouncilMap } from './council-map';
import { hasCustomSuburb } from './council-map/util';
import { useGetCouncilStatsQuery, useGetCouncilSuburbsQuery } from './graphql.generated';
import { useSuburbHeatByPlace } from './hooks';
import { MetricCard } from './metrics';
import styles from './styles.module.css';

const materialsOptions = Object.entries(materialTypeFormat)
	.map(([material, details]) => ({
		label: `${details.emoji} ${details.label}`,
		value: material,
	}))
	.filter((material) => ['SOFT_PLASTIC', 'TEXTILES'].includes(material.value))
	.sort((a, b) => a.label.localeCompare(b.label));

const viewOptions = [
	{ label: 'All', value: 'all' },
	// { label: 'Postcode', value: 'postcode' },
	{ label: 'Suburb', value: 'suburb' },
];

export type DashboardMetrics = 'accountsCreated' | 'scanEvents' | 'subscriptions';

export function CouncilDashboardPage() {
	const { councilId } = useParams({ from: '/council/$councilId' });
	const { view, startDate, endDate, material, suburb, selectedMetric /*postcode*/ } = useSearch({
		from: '/council/$councilId/dashboard',
	});
	const currentView = (view ?? 'all') as 'all' | 'suburb';
	const currentMetric = selectedMetric ?? 'accountsCreated';
	const [currentStartDate, setCurrentStartDate] = useState<string | null>(startDate ?? null);
	const [currentEndDate, setCurrentEndDate] = useState<string | null>(endDate ?? null);
	useEffect(() => {
		setCurrentStartDate(startDate ?? null);
		setCurrentEndDate(endDate ?? null);
	}, [startDate, endDate]);

	const navigate = useNavigate();

	const { data: councilData, loading: councilLoading } = useGetCouncilSuburbsQuery({
		variables: {
			councilId,
		},
	});
	const council = councilData?.council;
	const suburbsForCouncil = useMemo(() => councilData?.council?.suburbs ?? [], [councilData]);
	const selectedSuburb = suburbsForCouncil.find((s) => s.id === suburb);

	const suburbPlaceIds = suburbsForCouncil.map((suburb) => suburb.placeId).filter(hasValue);
	const customSuburbs = suburbsForCouncil
		.filter((suburb) => suburb.isCustomMapFeature)
		.filter(hasCustomSuburb);
	const suburbOptions = suburbsForCouncil
		.map((suburb) => ({
			label: suburb.displayName,
			value: suburb.id,
		}))
		.sort((a, b) => a.label.localeCompare(b.label));

	const { data: statsData, loading: statsLoading } = useGetCouncilStatsQuery({
		variables: {
			councilId: parseInt(councilId),
			startDate,
			endDate,
			material: material as MaterialType | undefined,
		},
		skip: !council?.placeId,
	});

	const councilStats = statsData?.getCouncilStats?.details;
	const currentStats =
		currentView === 'all'
			? councilStats
			: statsData?.getCouncilStats?.suburbs.find((s) => s.suburbId.toString() === suburb)?.details;

	// "Heat of places" means rank all suburbs from the lowest to highest value, and assign a score between to 0 and 1
	const heatOfPlaces = useSuburbHeatByPlace({
		currentView,
		currentMetric,
		suburbStatRecords: statsData?.getCouncilStats?.suburbs ?? [],
		allKnownSuburbs: council?.suburbs ?? [],
	});

	const dateRange: [string, string] | undefined = useMemo(() => {
		if (currentStartDate && currentEndDate) {
			return [currentStartDate, currentEndDate];
		}
		return undefined;
	}, [currentStartDate, currentEndDate]);

	const navigatePlusSearch = useCallback(
		(newParams: Record<string, string | undefined>) => {
			navigate({
				to: '.',
				search: (current) => ({ ...(current as object), ...newParams }),
				replace: true,
			});
		},
		[navigate]
	);
	const chooseMetric = useCallback(
		(selectedMetric: DashboardMetrics) => () => navigatePlusSearch({ selectedMetric }),
		[navigatePlusSearch]
	);

	const onSuburbSelected = useCallback(
		(placeId: string) => {
			const suburbMatch = suburbsForCouncil.find((suburb) => suburb.placeId === placeId);
			if (suburbMatch) {
				navigatePlusSearch({ suburb: suburbMatch.id, view: 'suburb' });
			}
		},
		[navigatePlusSearch, suburbsForCouncil]
	);

	if (councilLoading || statsLoading) {
		return <LoadingOverlay visible={true} />;
	}

	if (council && (council.placeId === null || council.placeId === undefined)) {
		return (
			<Flex h="100%" w="100%" direction="column" justify="center" align="center">
				<Text>This council is not configured for reporting yet. Please contact support.</Text>
			</Flex>
		);
	}

	return (
		<Flex h="100%" w="100%" pos="absolute" pb="60px" mt="-16px" ml="-16px" direction="column">
			<Flex p="md" gap="md" align="center" w="100%" className={styles.filter}>
				<Text>Filters</Text>
				<Divider orientation="vertical" />
				<Flex gap="md" align="center">
					<DatePickerInput
						type="range"
						placeholder="Select date range"
						label={'Date range'}
						value={dateRange}
						onChange={(value) => {
							const [startDate, endDate] = value;
							setCurrentStartDate(startDate);
							setCurrentEndDate(endDate);
							if (startDate && endDate) {
								navigatePlusSearch({ startDate, endDate });
							} else if (startDate === null && endDate === null) {
								navigatePlusSearch({ startDate: undefined, endDate: undefined });
							}
						}}
					/>
				</Flex>
				<Flex gap="md" align="center">
					<Select
						data={materialsOptions}
						placeholder="Select material"
						label={'Material'}
						value={material}
						onChange={(value) => {
							navigatePlusSearch({ material: value ?? undefined });
						}}
					/>
				</Flex>
				<Button
					variant="outline"
					color="black"
					size="md"
					leftSection={<IconX />}
					onClick={() => {
						navigatePlusSearch({
							startDate: undefined,
							endDate: undefined,
							material: undefined,
						});
					}}
				>
					Clear filters
				</Button>
			</Flex>
			<Flex w="100%" h="100%" className={styles.contentAreas}>
				<Flex h="100%" direction="column" w="350px" gap="md" p="lg" className={styles.leftNav}>
					<Flex direction="column" gap="sm" mih="180px">
						<Input.Label>Viewing</Input.Label>
						<SegmentedControl
							data={viewOptions}
							fullWidth
							radius="md"
							size="md"
							variant="filled"
							classNames={{
								label: styles.viewingLabel,
							}}
							value={currentView}
							onChange={(value) => {
								const newModeIsAll = value === 'all';
								navigatePlusSearch({
									view: value,
									suburb: newModeIsAll ? undefined : suburb,
								});
							}}
						/>
						{currentView === 'suburb' && (
							<Select
								data={suburbOptions}
								placeholder="Select suburb"
								label={'Current Suburb'}
								value={suburb}
								onChange={(value) => {
									navigatePlusSearch({ suburb: value ?? undefined });
								}}
							/>
						)}
					</Flex>
					<Divider size="md" />
					<Flex direction="column" gap="lg">
						<Text fw="bold" fz="lg">
							Metrics:
						</Text>
						<MetricCard
							title="Accounts created"
							value={currentStats?.accountsCreated.toString() ?? '-'}
							active={currentMetric === 'accountsCreated'}
							onClick={chooseMetric('accountsCreated')}
							clickable={currentView !== 'all'}
						/>
						<MetricCard
							title="Scan events"
							value={currentStats?.scanEvents.toString() ?? '-'}
							active={currentMetric === 'scanEvents'}
							onClick={chooseMetric('scanEvents')}
							clickable={currentView !== 'all'}
						/>
						<MetricCard
							title="KGs Diverted"
							value={currentStats?.kgDiverted?.toFixed(2) ?? '-'}
							active={currentMetric === 'scanEvents'}
							onClick={chooseMetric('scanEvents')}
							clickable={currentView !== 'all'}
						/>
					</Flex>
				</Flex>
				<CouncilMap
					councilPlaceId={council?.placeId ?? ''}
					suburbPlaceIds={suburbPlaceIds}
					customSuburbs={customSuburbs}
					onSuburbSelected={onSuburbSelected}
					selectedSuburbPlaceId={selectedSuburb?.placeId}
					heatOfPlaces={heatOfPlaces}
					className={styles.map}
				/>
			</Flex>
		</Flex>
	);
}
